import React from 'react';
import type { MarketingLabKey } from '../../../context/TenantContext';
import { ESSAY_SEQUENCE, PRACTICE_SEQUENCE } from './content';

/**
 * Essay and Practice Labs, as a numbered sequence of rendered panels.
 *
 * ## Why this replaced the capture-and-switcher
 *
 * Both sections used to be one phone capture with a row of tabs under it.
 * Three things were wrong with that, and they compound:
 *
 * 1. **The capture could not be read.** Every capture in this family is a
 *    390pt phone, and the ceiling rule (never render one wider than it was
 *    taken at — see `Shot.tsx`) correctly held it to 390px inside a 1280px
 *    column. At that size the screen is texture, not evidence. The sentence
 *    that actually sold the product sat underneath it in 15px grey.
 * 2. **A tabbed switcher is a carousel.** It hid five sixths of the evidence
 *    behind a click, and on a marketing page most people click nothing — so
 *    whatever was behind tab two was, in practice, not on the page.
 * 3. **The empty column.** A 390px picture in a 1280px row left 70% of the
 *    line blank beside an 874px-tall image. Essay and Practice were 42% of
 *    the page between them and sold the least of any section on it.
 *
 * Drawing the proof in HTML answers all three at once: no capture ceiling, so
 * it can be any size; nothing hidden; the width gets used. It also stops the
 * page going stale the day the product's own UI is restyled, and a screen
 * reader can read it.
 *
 * ## What it costs
 *
 * The product's numbers now live in a second place, and nothing enforces that
 * they still match the product. They are a worked example, not live data —
 * if the marking model changes what an L3 is worth, this is a hand edit.
 * That is the trade for legibility and it should be checked when the
 * assessment side changes.
 *
 * ## Not tenant-overridable, on purpose
 *
 * Every other string on this page falls back through `branding.marketing`.
 * These do not (see the note over the constants in `content.ts`): the panels
 * are one worked example of one real NSC question and the parts have to agree
 * with each other. Field-by-field overrides invite an example that
 * contradicts itself.
 *
 * The sequence is a spine on desktop and a horizontal peek-scroll under
 * 900px — a carousel, but the honest kind: the next card's edge shows, so it
 * says there is more rather than hiding it. See `.rv-seq` in app.css.
 */

const Step: React.FC<{ n: string; title: string; note?: string; children: React.ReactNode }> = ({
  n,
  title,
  note,
  children,
}) => (
  <div className="rv-seq-item">
    <span className="rv-seq-dot" aria-hidden="true">
      {n}
    </span>
    <div className="rv-seq-card">
      <p className="rv-seq-step">
        <b>{title}</b>
        {note && <span> · {note}</span>}
      </p>
      {children}
    </div>
  </div>
);

/** Two columns on desktop, stacked below it. */
const Split: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="rv-seq-split">{children}</div>
);

const PracticeSequence: React.FC = () => {
  const [one, two, three, four] = PRACTICE_SEQUENCE.steps;
  return (
    <>
      <div className="rv-seq">
        <Step n={one.n} title={one.title} note={one.note}>
          <Split>
            <div>
              <blockquote className="rv-seq-src">{one.source.text}</blockquote>
              <p className="rv-seq-cite">{one.source.cite}</p>
              <div className="rv-seq-qmeta">
                <span className="rv-seq-qid">{one.question.id}</span>
                <span className="rv-seq-qkind">{one.question.kind}</span>
                <span className="rv-seq-qmarks">{one.question.marks}</span>
              </div>
              <p className="rv-seq-qtext">{one.question.text}</p>
            </div>
            <div>
              <div className="rv-seq-opts">
                {one.question.options.map((o) => (
                  <span
                    className="rv-seq-opt"
                    key={o.key}
                    data-right={'correct' in o && o.correct ? 'true' : undefined}
                  >
                    <b>{o.key}.</b>
                    <span>{o.text}</span>
                    {'correct' in o && o.correct && (
                      <span className="rv-seq-tick" aria-hidden="true">
                        ✓
                      </span>
                    )}
                  </span>
                ))}
              </div>
              <div className="rv-seq-reason">
                <p className="rv-seq-reason-h">✓ {one.reason.heading}</p>
                <p>{one.reason.body}</p>
              </div>
            </div>
          </Split>
        </Step>

        <Step n={two.n} title={two.title} note={two.note}>
          <Split>
            <div>
              <div className="rv-seq-cue">
                <span aria-hidden="true">?</span>
                {two.cue}
              </div>
            </div>
            <ol className="rv-seq-steps">
              {two.steps.map((s, i) => (
                <li key={s.lead}>
                  <i aria-hidden="true">{i + 1}</i>
                  <span>
                    <b>{s.lead}</b> {s.rest}
                  </span>
                </li>
              ))}
            </ol>
          </Split>
        </Step>

        <Step n={three.n} title={three.title} note={three.note}>
          <Split>
            <div className="rv-seq-bars">
              {three.bars.map((b) => (
                <div className="rv-seq-bar" data-state={b.state} key={b.name}>
                  <span className="rv-seq-bar-name">{b.name}</span>
                  <span className="rv-seq-bar-num">{b.score}</span>
                  <span className="rv-seq-bar-track">
                    <span className="rv-seq-bar-fill" style={{ width: `${b.pct}%` }} />
                  </span>
                </div>
              ))}
            </div>
            <p className="rv-seq-verdict">
              {three.verdict.lead} <b>{three.verdict.strong}</b> {three.verdict.rest}
            </p>
          </Split>
        </Step>

        <Step n={four.n} title={four.title} note={four.note}>
          <Split>
            <div>
              <p className="rv-seq-markq">{four.mark.question}</p>
              <div className="rv-seq-markrow">
                {four.mark.options.map((o, i) => (
                  <span key={o} data-picked={i === four.mark.picked ? 'true' : undefined}>
                    {o}
                  </span>
                ))}
              </div>
            </div>
            <div className="rv-seq-miss">
              <p className="rv-seq-miss-h">✗ {four.miss.heading}</p>
              <p>{four.miss.body}</p>
            </div>
          </Split>
        </Step>
      </div>
      <p className="rv-cap rv-seq-foot">{PRACTICE_SEQUENCE.foot}</p>
    </>
  );
};

const EssaySequence: React.FC = () => {
  const [one, two, three, four] = ESSAY_SEQUENCE.steps;
  return (
    <div className="rv-seq">
      <Step n={one.n} title={one.title} note={one.note}>
        <p className="rv-seq-quote">
          {one.quote.parts.map((part, i) =>
            typeof part === 'string' ? (
              <React.Fragment key={i}>{part}</React.Fragment>
            ) : (
              <span key={i}>{part.accent}</span>
            ),
          )}
        </p>
      </Step>

      <Step n={two.n} title={two.title} note={two.note}>
        <Split>
          <div>
            <p className="rv-seq-best-h">{two.bestLine.heading}</p>
            <blockquote className="rv-seq-best">{two.bestLine.body}</blockquote>
          </div>
          <div>
            <p className="rv-seq-sentence">
              {two.pinned.before}
              <span className="rv-seq-mark">{two.pinned.highlight}</span>
            </p>
            <div className="rv-seq-pin">
              <p className="rv-seq-pin-h">{two.pinned.heading}</p>
              <p>{two.pinned.body}</p>
            </div>
          </div>
        </Split>
      </Step>

      <Step n={three.n} title={three.title} note={three.note}>
        <Split>
          <div>
            <p className="rv-seq-stat">
              <span className="rv-seq-stat-n">{three.stat.value}</span>
              <span className="rv-seq-stat-l">
                {three.stat.label.split('\n').map((line, i) => (
                  <React.Fragment key={line}>
                    {i > 0 && <br />}
                    {line}
                  </React.Fragment>
                ))}
              </span>
            </p>
            <p className="rv-cap">{three.stat.body}</p>
          </div>
          <div className="rv-seq-pin">
            <p className="rv-seq-pin-h">{three.pinned.heading}</p>
            <p>{three.pinned.body}</p>
          </div>
        </Split>
      </Step>

      <Step n={four.n} title={four.title} note={four.note}>
        <Split>
          <div className="rv-seq-bands">
            {four.bands.map((b) => (
              <span className="rv-seq-band" key={b.name} data-state={'state' in b ? b.state : undefined}>
                <span className="rv-seq-band-bar" />
                <span className="rv-seq-band-n">{b.name}</span>
              </span>
            ))}
          </div>
          <p className="rv-cap">{four.bandsBody}</p>
        </Split>
      </Step>
    </div>
  );
};

/** Renders the sequence for the labs that have one; null for the rest. */
export const LabSequence: React.FC<{ lab: MarketingLabKey }> = ({ lab }) => {
  if (lab === 'practice') return <PracticeSequence />;
  if (lab === 'essay') return <EssaySequence />;
  return null;
};
